import React from 'react';
import styled from 'styled-components';

import { BodyText, DetailText, FlexCol } from '@minecraft.atoms';
import { TalentScoutProfileFragment, TalentScoutSeekingRepresentationFragment } from '@minecraft.graphql-operations';
import { isNotNullish } from '@minecraft.utils';
import { useTranslation } from '@blocs.i18n';
import { BaseballCard } from '@minecraft.molecules';

export const UnorderedList = styled.ul`
  line-height: 1rem;
  margin-block: 0;
  padding-inline-start: 1rem;
`;

export const seekingRepresentationIdToLabelLookup = {
  // id: translation key
  '1': 'common:label.seekingRepresentation.theatricalRep',
  '2': 'common:label.seekingRepresentation.commercialRep',
  '3': 'common:label.seekingRepresentation.printRep',
  '4': 'common:label.seekingRepresentation.danceRep',
  '5': 'common:label.seekingRepresentation.hostingRep',
  '6': 'common:label.seekingRepresentation.voiceOverRep',
  '7': 'common:label.seekingRepresentation.kidsOrYouthRep',
  '8': 'common:label.seekingRepresentation.manager',
};
const useSeekingRepresentationText = (seekingRepresentation: TalentScoutSeekingRepresentationFragment[]) => {
  const { t } = useTranslation();

  return seekingRepresentation
    .map(({ id }) =>
      id in seekingRepresentationIdToLabelLookup ? t(seekingRepresentationIdToLabelLookup[id]) : undefined
    )
    .filter(isNotNullish);
};

export const SeekingRepresentation: React.FC<{
  seekingRepresentation?: TalentScoutSeekingRepresentationFragment[];
}> = ({ seekingRepresentation }) => {
  const { t } = useTranslation();
  const text = useSeekingRepresentationText(seekingRepresentation ?? []);

  return (
    <>
      {seekingRepresentation && seekingRepresentation.length > 0 && (
        <FlexCol gap="1">
          <BodyText fontWeight="medium">{t('common:label.seekingRepresentation.seeking')}</BodyText>
          <UnorderedList lineHeight="ultraCondensed">
            {text.map((txt) => (
              <li key={txt.replace(' ', '-')}>
                <DetailText>{txt}</DetailText>
              </li>
            ))}
          </UnorderedList>
        </FlexCol>
      )}
    </>
  );
};

const useSeekingRepresentationBannerText = (seekingRepresentation: TalentScoutSeekingRepresentationFragment[]) => {
  const { t } = useTranslation();
  const seekingRepresentationText = useSeekingRepresentationText(seekingRepresentation ?? []);

  const multipleRepTypesText = t('common:label.seekingRepresentation.multipleRepTypes');
  const seekingText = t('common:label.seekingRepresentation.seeking');

  if (seekingRepresentationText.length === 0) {
    return undefined;
  }

  if (seekingRepresentationText.length === 1) {
    return `${seekingText} ${seekingRepresentationText[0]}`;
  }

  return `${seekingText} ${multipleRepTypesText}`;
};

export const SeekingRepresentationBanner = (profile: Pick<TalentScoutProfileFragment, 'seekingRepresentation'>) => {
  const bannerText = useSeekingRepresentationBannerText((profile as any).seekingRepresentation ?? []);

  return (
    <>
      {bannerText ? (
        <BaseballCard.Banner backgroundColor="comboboxToken">
          <DetailText lineHeight="expanded" testId="seekingrepresentation-text">
            <span style={{ fontSize: '0.5625rem' }}>{bannerText}</span>
          </DetailText>
        </BaseballCard.Banner>
      ) : (
        <BaseballCard.Banner data-testid="seekingrepresentation-placeholder" />
      )}
    </>
  );
};
